import React, { useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import TruncatedSeq from "../../../components/TruncatedSeq";
import store from "../../../state/store";
import { constructVariantQueryParams } from "../../../common/helpers/helpers";

/** For ambiguous c. position queries that end up having many results. e.g. TP53:c.147 */
/** For multiple aminoacid or c.notation results e.g. TP53:G187S (aminoacid) or BAIAP2L2:c.395_436del (c.notation) * */

function IntermediateScreen({
  multipleResults,
  variantQueryMode,
  varsomeHost,
  genome,
  isIntermediate,
  hasNext,
}) {
  useEffect(() => {
    if (isIntermediate === "rsid" && hasNext) {
      const results = multipleResults;

      store.dispatch({
        type: "SET_CURRENT_VARIANT",
        result: { variantResult: results[0] },
      });
      store.dispatch({
        type: "SET_NEXT",
        result: { hasNext: false },
      });
    }
  }, []);

  if (isIntermediate === "position") {
    const positions = multipleResults;
    const showLegend = (positions || []).find((x) => x.canonical);
    const params = constructVariantQueryParams(variantQueryMode);

    return (
      <div className="container" style={{ margin: "2rem auto" }}>
        <h5>Your query results in the following genomic positions:</h5>
        <div className="tw-ml-4">
          <table className="tw-w-full tw-mb-8">
            <tbody>
              {positions.map((pos, i) => {
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #eceeef" }}>
                    <td className="tw-pr-12" style={{ padding: "0.3rem 0" }}>
                      <a
                        href={`${varsomeHost}/variant/${genome.name}/${pos.transcript}?${params}`}
                        className="tw-cursor-pointer"
                      >
                        {pos.transcript}
                      </a>
                      {pos.canonical && <sup> †</sup>}
                    </td>
                    <td style={{ padding: "0.3rem 1rem" }}>{pos.pos}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {showLegend && (
          <div
            className="tw-w-full tw-text-center"
            style={{
              margin: "0 0 2rem 0",
            }}
          >
            † - Canonical transcript
          </div>
        )}
      </div>
    );
  }

  if (isIntermediate === "aminoacid") {
    const transcripts = multipleResults;
    const transcriptQuery = transcripts[0].query;
    const reg = /\((.*?)\)/g;
    const parenthesisText = transcriptQuery.match(reg);
    const text = parenthesisText[0].replace("(", "").replace(")", "");
    const transcriptReg = /^((NR_)|(NM_)|(ENST))\d+(\.\d+)?$/i;
    const matchesTranscript = text.match(transcriptReg);

    const geneSymbol = matchesTranscript
      ? transcripts[0].query.split("(")[0].toUpperCase()
      : transcripts[0].query.split("(")[1].split(")")[0].toUpperCase();

    const showLegend = transcripts.find((x) => x.canonical);

    const params = constructVariantQueryParams(variantQueryMode);

    return (
      <div className="container-fluid" style={{ margin: "2rem auto" }}>
        <h5>
          {`Gene ${geneSymbol} has ${transcripts.length} transcripts containing variants that match your query:`}
        </h5>
        <div className="tw-ml-4">
          <table className="tw-w-full tw-mb-8">
            <tbody>
              {transcripts.map((t, i) => {
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #eceeef" }}>
                    <td style={{ padding: "0.3rem 0", paddingRight: "3rem" }}>
                      <a
                        className="tw-cursor-pointer"
                        href={`${varsomeHost}/variant/${genome.name}/${t.query}?${params}`}
                      >
                        {t.query}
                      </a>
                      {t.canonical && <sup> †</sup>}
                    </td>
                    <td style={{ padding: "0.3rem 1rem" }}>
                      {t.variants.map((v, j) => {
                        return (
                          <span key={`${i}-${j}`}>
                            {`${v.chromosome}-${v.pos}-`}
                            <TruncatedSeq seq={v.ref} />
                            -
                            <TruncatedSeq seq={v.alt} />
                            {j < t.variants.length - 1 && ", "}
                          </span>
                        );
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {showLegend && (
          <div className="tw-text-center tw-w-full tw-mx-0 tw-mb-8 tw-mt-0">
            † - Canonical transcript in RefSeq or Ensembl database
          </div>
        )}
      </div>
    );
  }

  return null;
}

IntermediateScreen.propTypes = {
  genome: PropTypes.shape({ name: PropTypes.string, id: PropTypes.number }),
  varsomeHost: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  multipleResults: PropTypes.arrayOf(PropTypes.any),
  variantQueryMode: PropTypes.shape({}),
  isIntermediate: PropTypes.string,
  hasNext: PropTypes.bool,
};

IntermediateScreen.defaultProps = {
  genome: {},
  varsomeHost: "",
  multipleResults: [],
  variantQueryMode: null,
  isIntermediate: "",
  hasNext: false,
};

const mapStateToProps = (state) => ({
  multipleResults: state.multipleResults,
  genome: state.genome,
  varsomeHost: state.varsomeHost,
  variantQueryMode: state.variantQueryMode,
  isIntermediate: state.isIntermediate,
  hasNext: state.hasNext,
});

export default connect(mapStateToProps)(IntermediateScreen);
